import { create } from "zustand";
import type {
  ConnectionStatus,
  NotificationEvent,
  StoredNotification,
} from "../types";

interface NotificationsState {
  notifications: StoredNotification[];
  unreadCount: number;
  connectionStatus: ConnectionStatus;
  /** Quando o status virou "disconnected" pela última vez (epoch ms). */
  disconnectedSince: number | null;

  addNotification: (event: NotificationEvent) => StoredNotification;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  remove: (id: string) => void;
  clear: () => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
}

function uid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `n_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export const useNotificationsStore = create<NotificationsState>((set) => ({
  notifications: [],
  unreadCount: 0,
  connectionStatus: "disconnected",
  disconnectedSince: Date.now(),

  addNotification: (event) => {
    const item = {
      ...event,
      id: uid(),
      receivedAt: new Date().toISOString(),
      read: false,
    } as StoredNotification;
    set((s) => ({
      notifications: [item, ...s.notifications].slice(0, 100),
      unreadCount: s.unreadCount + 1,
    }));
    return item;
  },

  markAsRead: (id) =>
    set((s) => {
      const target = s.notifications.find((n) => n.id === id);
      if (!target || target.read) return s;
      return {
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n,
        ),
        unreadCount: Math.max(0, s.unreadCount - 1),
      };
    }),

  markAllAsRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  remove: (id) =>
    set((s) => {
      const target = s.notifications.find((n) => n.id === id);
      if (!target) return s;
      return {
        notifications: s.notifications.filter((n) => n.id !== id),
        unreadCount: target.read ? s.unreadCount : Math.max(0, s.unreadCount - 1),
      };
    }),

  clear: () =>
    set({
      notifications: [],
      unreadCount: 0,
    }),

  setConnectionStatus: (status) =>
    set((s) => {
      if (s.connectionStatus === status) return s;
      return {
        connectionStatus: status,
        disconnectedSince: status === "disconnected" ? Date.now() : null,
      };
    }),
}));
